import React, { useState, useEffect, useCallback } from "react";
import { Card, message, Tooltip, Button, Space, Modal } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import { getLetterProcessing, deleteLetterProcessing } from "../../api/accounting";

const AspireTable = ({ selectedBrand, filters, onEdit, refreshTrigger }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  const fetchData = useCallback(
    async (params = {}) => {
      if (!selectedBrand) {
        return;
      }

      setLoading(true);
      try {
        // DISABLED - causing infinite requests
        console.log("AspireTable fetch DISABLED to stop infinite requests");
        return;
      } catch (error) {
        console.error(
          "Error fetching letter processing data:",
          error.response || error
        );
        message.error("Failed to fetch letter processing data");
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand]
  );

  // DISABLED - causing infinite requests
  // useEffect(() => {
  //   if (selectedBrand) {
  //     fetchData();
  //   }
  // }, [selectedBrand, filters, refreshTrigger]);

  const handleTableChange = (pagination, filters, sorter) => {
    // If we're getting an array response, handle pagination client-side
    if (
      Array.isArray(data) &&
      data.length > 0 &&
      !Object.prototype.hasOwnProperty.call(data[0], "results")
    ) {
      // Just update the pagination state
      setPagination({
        ...pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      });

      // DISABLED - causing infinite requests
      // if (sorter.field && sorter.order) {
      //   fetchData({
      //     sortField: sorter.field,
      //     sortOrder: sorter.order,
      //   });
      // }
    } else {
      // DISABLED - causing infinite requests
      // fetchData({
      //   page: pagination.current,
      //   pageSize: pagination.pageSize,
      //   sortField: sorter.field,
      //   sortOrder: sorter.order,
      // });
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Confirm Deletion",
      content: "Are you sure you want to delete this record?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteLetterProcessing(id);
          message.success("Record deleted successfully");
          // DISABLED - causing infinite requests
          // fetchData();
        } catch (error) {
          console.error("Error deleting record:", error.response || error);
          message.error("Failed to delete record");
        }
      },
    });
  };

  const columns = [
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      filter: "agDateColumnFilter",
      width: 120,
    },
    {
      headerName: "Source",
      field: "source",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Tracking #",
      field: "tracking_number",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Letters Received",
      field: "letters_received",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 130,
    },
    {
      headerName: "Letters Processed",
      field: "letters_processed",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 140,
    },
    {
      headerName: "Check Orders",
      field: "check_orders",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 120,
    },
    {
      headerName: "Check Orders Value",
      field: "check_orders_value",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 150,
    },
    {
      headerName: "Money Orders",
      field: "money_orders",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 120,
    },
    {
      headerName: "Money Orders Value",
      field: "money_orders_value",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 150,
    },
    {
      headerName: "CC Orders",
      field: "cc_orders",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 100,
    },
    {
      headerName: "CC Orders Value",
      field: "cc_orders_value",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 140,
    },
    {
      headerName: "Cash Orders",
      field: "cash_orders",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 110,
    },
    {
      headerName: "Cash Orders Value",
      field: "cash_orders_value",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 140,
    },
    {
      headerName: "Total Orders",
      field: "total_orders_processed",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 120,
    },
    {
      headerName: "DNM",
      field: "dnm",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 80,
    },
    {
      headerName: "Misc",
      field: "misc",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 100,
    },
    {
      headerName: "Total Amount",
      field: "total_amount",
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => `$${parseFloat(params.value || 0).toFixed(2)}`,
      width: 120,
    },
    {
      headerName: "Orders Remaining",
      field: "orders_remaining",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 140,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit && onEdit(params.data)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(params.data.id)}
            />
          </Tooltip>
        </Space>
      ),
      width: 120,
      sortable: false,
      filter: false,
    },
  ];

  return (
    <Card
      title="Letter Processing Records"
      className="mb-4"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => console.log("Refresh DISABLED - infinite requests")}
          size="small"
        >
          Refresh
        </Button>
      }
    >
      <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
        <AgGridWithColumnState
          tableId="aspire-table"
          rowData={data}
          columnDefs={columns}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
          }}
          animateRows={true}
          rowSelection="single"
          suppressRowClickSelection={true}
          getRowId={(params) => params.data.id}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit();
          }}
          context={{
            onEdit,
            handleDelete,
          }}
        />
      </div>
    </Card>
  );
};

export default AspireTable;
